import { put, select } from 'redux-saga/effects';
import {
  ChatImageUploadPreparedItem,
  ChatMessageImage,
  ChatMessagePayload,
  ChatMessageUser,
  NewMessagePayload,
  PendingChatImage,
} from 'features/chat/types';
import { optimisticChatMessageAdded } from 'features/chat/chatSlice';
import { selectLocalUser, selectUserById } from 'features/users/usersSlice';
import { User } from 'features/users/types';

export interface AddOptimisticChatMessageArgs {
  messageId: string;
  message: string;
  pendingImages: PendingChatImage[];
  preparedImages: ChatImageUploadPreparedItem[];
  targetUserId?: NewMessagePayload['targetUserId'];
  privateGroup?: NewMessagePayload['privateGroup'];
  parentId?: NewMessagePayload['parentId'];
}

const toChatMessageUser = (user: User): ChatMessageUser => ({
  id: user.id,
  name: user.name,
  avatarColor: user.avatarColor,
  avatarUrl: user.avatarUrl,
  initials: user.initials,
  externalId: user.externalId,
});

export function* addOptimisticChatMessageSaga(args: AddOptimisticChatMessageArgs) {
  const {
    messageId,
    message,
    pendingImages,
    preparedImages,
    targetUserId,
    privateGroup,
    parentId,
  } = args;

  const localUser: User | undefined = yield select(selectLocalUser);
  if (!localUser) {
    return;
  }

  const targetUser: User | undefined = targetUserId
    ? yield select(selectUserById, targetUserId)
    : undefined;

  const optimisticImages: ChatMessageImage[] = pendingImages.map((pending, idx) => ({
    id: preparedImages[idx].file_id,
    url: pending.previewUrl,
    thumbnailUrl: pending.previewUrl,
    fileName: pending.name,
    localId: pending.localId,
  }));

  const optimisticPayload: ChatMessagePayload = {
    message,
    privateGroup,
    id: messageId,
    date: new Date().toISOString(),
    local: true,
    user: toChatMessageUser(localUser),
    targetUser: targetUser ? toChatMessageUser(targetUser) : undefined,
    images: optimisticImages,
    optimistic: true,
    reply: Boolean(parentId),
    parentId,
  };

  yield put(optimisticChatMessageAdded(optimisticPayload));
}
